import React, { useState } from "react";
import {
  Avatar, Box, Menu, MenuItem, ListItemAvatar, ListItemText, Tooltip, TextField, InputAdornment, Typography,
} from "@mui/material";
import { Search } from "lucide-react";
import { avatarColor, initials } from "../utils/avatarColor";

/**
 * Jira-style assignee filter: a handful of avatars shown inline, click one
 * to filter to that person (click again to clear); anything beyond `max`
 * collapses into a "+N" avatar that opens a searchable list. The current
 * selection is always kept visible in the inline row -- if it was picked
 * from the overflow list, it's pinned to the front so it never disappears
 * once the menu closes.
 */
export default function AssigneeAvatarFilter({ employees, selectedId, onChange, max = 5 }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [query, setQuery] = useState("");

  const all = employees || [];
  const selected = selectedId ? all.find((e) => e.id === selectedId) : null;

  let visible = all.slice(0, max);
  if (selected && !visible.some((e) => e.id === selectedId)) {
    visible = [selected, ...visible.slice(0, Math.max(0, max - 1))];
  }
  const overflow = all.filter((e) => !visible.some((v) => v.id === e.id));
  const filteredOverflow = overflow.filter((e) => (e.name || e.email || "").toLowerCase().includes(query.toLowerCase()));

  const handlePick = (id) => {
    onChange(selectedId === id ? null : id);
    setAnchorEl(null);
    setQuery("");
  };

  if (all.length === 0) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {visible.map((e) => (
        <Tooltip key={e.id} title={e.name || e.email}>
          <Avatar
            onClick={() => handlePick(e.id)}
            sx={{
              width: 30, height: 30, fontSize: 12, cursor: "pointer",
              bgcolor: avatarColor(e.name),
              border: selectedId === e.id ? "2px solid #1565C0" : "2px solid transparent",
              opacity: selectedId && selectedId !== e.id ? 0.45 : 1,
              transition: "opacity 0.15s, border-color 0.15s",
            }}
          >
            {initials(e.name)}
          </Avatar>
        </Tooltip>
      ))}

      {overflow.length > 0 && (
        <>
          <Tooltip title="More assignees">
            <Avatar
              onClick={(ev) => setAnchorEl(ev.currentTarget)}
              sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, cursor: "pointer", bgcolor: "action.selected", color: "text.primary" }}
            >
              +{overflow.length}
            </Avatar>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => { setAnchorEl(null); setQuery(""); }}>
            <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }} onKeyDown={(e) => e.stopPropagation()}>
              <TextField
                size="small" autoFocus placeholder="Search assignee..." value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> }}
              />
            </Box>
            {filteredOverflow.length === 0 && <MenuItem disabled>No matches</MenuItem>}
            {filteredOverflow.map((e) => (
              <MenuItem key={e.id} selected={selectedId === e.id} onClick={() => handlePick(e.id)}>
                <ListItemAvatar>
                  <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: avatarColor(e.name) }}>{initials(e.name)}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={e.name || e.email} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {selectedId && (
        <Typography
          variant="caption" color="text.secondary"
          onClick={() => onChange(null)}
          sx={{ cursor: "pointer", textDecoration: "underline", ml: 0.5 }}
        >
          Clear
        </Typography>
      )}
    </Box>
  );
}
